import { format } from "date-fns";

// Helper function to format numbers with trailing zeros removed
const formatNumber = (num: number, decimals?: number) => {
  const fixed = num.toFixed(decimals);
  return parseFloat(fixed).toString(); // This removes trailing zeros
};

export type TextColor =
  | "default"
  | "aqua"
  | "blue"
  | "red"
  | "green"
  | "grey"
  | "green2"
  | "red2";

export const IsTextColor = (value: string): value is TextColor => {
  return [
    "default",
    "aqua",
    "blue",
    "red",
    "green",
    "grey",
    "green2",
    "red2",
  ].includes(value);
};

function formatTinyNumber(
  n: number | string,
  decimals?: number,
  shouldFormat = true
): string {
  const num = typeof n === "string" ? Number(n) : n;

  const precision = decimals ?? 4;

  if (isNaN(num) || num >= 0.001) {
    return parseFloat(num.toFixed(precision)).toString();
  }

  const abs = Math.abs(num);
  const expMatch = abs.toExponential().match(/(\d(?:\.\d+)?)[eE]-(\d+)/);
  if (!expMatch || !shouldFormat)
    return parseFloat(num.toFixed(precision)).toString();

  const [, , exponentStr] = expMatch;
  const exponent = parseInt(exponentStr, 10);
  const zeroCount = exponent - 1;

  // Shift to get 2 significant digits, then round
  const shifted = Math.round(abs * 10 ** (zeroCount + 2));
  const digits = String(shifted).padStart(2, "0").slice(0, 2);

  return `0.0<sub>${zeroCount}</sub>${digits}`;
}

/**
 * Represents a formatted value with various display options
 */
export class FormattedValue {
  private originalValue: number;
  private formattedValue: string;
  private textColor: TextColor = "default";

  /**
   * Creates a new FormattedValue instance
   * @param value - The numeric value to format
   */
  constructor(value: number) {
    this.originalValue = value;
    this.formattedValue = Math.abs(value).toString();
  }

  /**
   * Sets the text color based on the value being positive, negative, or zero
   * @returns The FormattedValue instance for chaining
   */
  color({
    positive,
    negative,
    inherited,
  }: {
    positive?: TextColor;
    negative?: TextColor;
    inherited?: TextColor;
  }): FormattedValue {
    if (inherited) {
      this.textColor = inherited;
    } else if (this.originalValue >= 0 && positive) {
      this.textColor = positive;
    } else if (negative) {
      // we want sign in front of currency symbol, so the value sign should be removed to avoid duplicates
      this.textColor = negative;
    }

    return this;
  }

  private withSubscriptZeros(
    number: number,
    decimals?: number,
    shouldFormat?: boolean
  ) {
    return formatTinyNumber(number, decimals, shouldFormat);
  }

  /**
   * Formats the value as currency with a prefix
   * @param currency - Currency symbol to prefix (default: '$')
   * @returns The FormattedValue instance for chaining
   */
  currency(currency: string = "$"): FormattedValue {
    this.formattedValue = `${currency}${this.formattedValue}`;
    return this;
  }

  decimal(decimals?: number): FormattedValue {
    decimals = decimals ? decimals : this.originalValue < 1 ? 4 : 2;

    this.formattedValue = formatNumber(this.originalValue, decimals);
    return this;
  }
  /**
   * Formats the number with thousand separators and unit suffixes (K, M, B, T)
   * e.g. 1234 -> 1.23K, 1234567 -> 1.23M, 1234567890 -> 1.23B
   *
   * @param decimals - Number of decimal places to show (default: 2)
   * @param withSubZeros - When true, for numbers less than 1, displays leading zeros in subscript, or formats to 4 decimal points if greater than 0.0001
   *                      e.g. 0.000001234 -> 0.0<sub>4</sub>12
   * @returns The FormattedValue instance for chaining
   */
  unit(
    withSubZeros: boolean = false,
    decimals?: number,
    shouldFormat?: boolean
  ): FormattedValue {
    const value = Math.abs(this.originalValue);
    decimals = decimals ? decimals : value < 1 ? 4 : 2;

    if (value === null || value === undefined) return this;
    let formattedValue;

    if (withSubZeros && value < 1) {
      formattedValue = this.withSubscriptZeros(value, decimals, shouldFormat);
    } else if (value >= 1e12) {
      // Trillions
      formattedValue = `${formatNumber(value / 1e12, decimals)}T`;
    } else if (value >= 1e9) {
      // Billions
      formattedValue = `${formatNumber(value / 1e9, decimals)}B`;
    } else if (value >= 1e6) {
      // Millions
      formattedValue = `${formatNumber(value / 1e6, decimals)}M`;
    } else if (value >= 1e3) {
      // Thousands
      formattedValue = `${formatNumber(value / 1e3, decimals)}K`;
    } else {
      // Less than 1,000
      formattedValue = formatNumber(value, decimals);
    }
    this.formattedValue = formattedValue;
    return this;
  }

  /**
   * Appends a percent symbol to the formatted value
   * @returns The FormattedValue instance for chaining
   */
  percent(): FormattedValue {
    this.formattedValue = `${this.formattedValue}%`;

    return this;
  }

  /**
   * Gets the current text color based on the value
   *  .color() format needs to be called before, if you want the color to be applied
   * @returns The color value: 'default', 'red', 'green', 'blue, 'aqua'
   */
  getColor(): TextColor {
    return this.textColor;
  }

  getSign(): string {
    if (this.originalValue === 0) return "";
    return this.originalValue > 0 ? "+" : "-";
  }

  getTrendArrow(): string {
    if (this.originalValue === 0) return "";
    return this.originalValue > 0 ? "▲" : "▼";
  }

  sign(): FormattedValue {
    if (this.originalValue === 0) return this;
    const sign = this.originalValue > 0 ? "+" : "-";
    this.formattedValue = `${sign}${this.formattedValue}`;
    return this;
  }

  /**
   * Formats the value as a time duration with appropriate units (Hours, Mins, Days)
   * Expects the value to be in seconds
   * @returns The FormattedValue instance for chaining
   */
  time(
    { variant }: { variant: "short" | "long" } = { variant: "long" }
  ): FormattedValue {
    const value = this.originalValue;
    const shortMap = {
      Seconds: "s",
      Mins: "m",
      Hours: "h",
      Days: "d",
    };
    const longMap = {
      Seconds: " Seconds",
      Mins: " Minutes",
      Hours: " Hours",
      Days: " Days",
    };

    const resolvedMap = variant === "short" ? shortMap : longMap;
    if (value === null || value === undefined) return this;

    if (value < 60) {
      this.formattedValue = `${value}${resolvedMap.Seconds}`;
    } else if (value < 3600) {
      this.formattedValue = `${Math.round(value / 60)}${resolvedMap.Mins}`;
    } else if (value < 86400) {
      // Less than 24 hours
      this.formattedValue = `${Math.round(value / 3600)}${resolvedMap.Hours}`;
    } else {
      this.formattedValue = `${Math.round(value / 86400)}${resolvedMap.Days}`;
    }
    return this;
  }

  /**
   * Formats the value as a Date in "Month Year" format
   * Expects the value to be a timestamp in milliseconds (e.g., 1732230038000)
   * @returns The FormattedValue instance for chaining
   */
  date(): FormattedValue {
    const date = new Date(this.originalValue);

    if (!isNaN(date.getTime())) {
      this.formattedValue = format(date, "MMMM yyyy");
    } else {
      this.formattedValue = "Invalid Date";
    }
    return this;
  }

  /**
   * Converts the formatted value to a string
   * @returns The final formatted string value
   */
  toString(): string {
    return this.formattedValue?.toString() ?? "";
  }

  /**
   * A function that allows you to format the value in a custom way
   * @param formatCallback - A function that takes the original value and the formatted value and returns the new formatted value
   * @returns
   */
  format(
    /**
     * @returns The new formatted value
     */
    formatCallback: (params: {
      /** The original numeric value before any formatting
       * The one you passed through Format.from(value)
       *
       */
      originalValue: number;
      /** The current formatted value of the original value
       *
       * E.g. if you called Format.from(1234).decimals(), formattedValue will be 1.23K
       */
      formattedValue: string;
      /** The color value: 'default', 'red', 'green', 'blue' or 'aqua' */
      color: TextColor;
    }) => string
  ) {
    const result = formatCallback({
      originalValue: this.originalValue,
      formattedValue: this.formattedValue,
      color: this.textColor,
    });
    this.formattedValue = result;
    return this;
  }
}

/**
 * Utility class for creating formatted values
 */
export class Format {
  /**
   * Creates a new FormattedValue instance from a number
   * @param value - The numeric value to format
   * @returns A new FormattedValue instance
   */
  static from(value: number): FormattedValue {
    if (typeof value !== "number") {
      return new FormattedValue(0);
    }
    return new FormattedValue(value);
  }
}
